import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
    LouvreCurtainEditor2D,
    type Louvre,
    type LouvreCurtainCutouts,
} from "@dextall/louvre-curtain-cutout-2d-editor";
import { SelectionType } from "@dextall/panel-hooks-editor-2d";
import { ProcessingPane } from "../../../../processing-pane/processingPane";
import { rootElement } from "../../../../shared";
import { PanelEditorScope } from "../../../panels/panel-editor-scope";
import {
    createEditor2DLouvre,
    toEditorLouvreCurtainCircularCutouts,
    toEditorLouvreCurtainRectangularCutouts,
} from "../../../panels/panelLouvreSourceDefinitionFactory";
import { usePanelSelectedComponent } from "../usePanelSelectedComponentHook";
import { LouvreCutoutsEditorToolbar } from "./louvreCutoutsEditorToolbar";
import type { IApplicationEvent } from "../../../eventBus/eventDispatcher";
import eventBus from "../../../eventBus/eventDispatcher";

type Props = {
    panelEditorScope: PanelEditorScope;
};

type LouvreSelection = {
    panelId: string;
    louvreIndex: number;
};

export const LouvreCutoutsEditorDockingPanelContent = ({ panelEditorScope }: Props) => {
    const { panel, selection } = usePanelSelectedComponent(panelEditorScope);
    const [louvre, setLouvre] = useState<Louvre | null>(null);
    const [cutouts, setCutouts] = useState<LouvreCurtainCutouts>();
    const louvreSelectionRef = useRef<LouvreSelection | null>(null);
    const [isUnsaved, setIsUnsaved] = useState(false);
    const [isWithIntersections, setIsWithIntersections] = useState(false);
    const [isSaveInProgress, setIsSaveInProgress] = useState(false);

    useEffect(() => {
        if (panel && selection?.type === SelectionType.Louvre) {
            const panelLouvre = panel.louvres[selection.index];

            setLouvre(createEditor2DLouvre(panelLouvre.openingWidth, panelLouvre.openingHeight));
            setCutouts({
                rectangularCutouts: toEditorLouvreCurtainRectangularCutouts(panelLouvre.rectangularCutouts),
                circularCutouts: toEditorLouvreCurtainCircularCutouts(panelLouvre.circularCutouts),
            });
            if (
                louvreSelectionRef.current?.panelId !== panel.id ||
                louvreSelectionRef.current?.louvreIndex !== selection.index
            ) {
                setIsUnsaved(false);
                louvreSelectionRef.current = {
                    panelId: panel.id,
                    louvreIndex: selection.index,
                };
            }
        } else {
            setLouvre(null);
            setCutouts({
                rectangularCutouts: [],
                circularCutouts: [],
            });
            louvreSelectionRef.current = null;
            setIsUnsaved(false);
            setIsWithIntersections(false);
        }
        setIsSaveInProgress(false);
    }, [panel, selection]);

    useEffect(() => {
        const onLouvreCutoutsSaved = (event: IApplicationEvent<boolean>) => {
            setIsSaveInProgress(false);
            if (event.payload) {
                setIsUnsaved(false);
            }
        };

        const onLouvreCutoutsSaveStarted = () => {
            setIsSaveInProgress(true);
        };

        eventBus.addEventListener("Dextall.PanelTypes.UI.LouvreCutoutsSaved", onLouvreCutoutsSaved);
        eventBus.addEventListener("Dextall.PanelTypes.UpdateLouvreCutouts", onLouvreCutoutsSaveStarted);
        return () => {
            eventBus.removeEventListener("Dextall.PanelTypes.UI.LouvreCutoutsSaved", onLouvreCutoutsSaved);
            eventBus.removeEventListener("Dextall.PanelTypes.UpdateLouvreCutouts", onLouvreCutoutsSaveStarted);
        };
    }, []);

    if (!(louvre && cutouts && panel && selection?.type === SelectionType.Louvre)) {
        return null;
    }

    const updateCutouts = (newCutouts: LouvreCurtainCutouts) => {
        setCutouts(newCutouts);
        setIsUnsaved(true);
    };

    return (
        <>
            <LouvreCurtainEditor2D
                louvre={louvre}
                cutouts={cutouts}
                onCutoutsChanged={updateCutouts}
                onCutoutsIntersectionsChanged={setIsWithIntersections}
            />
            <LouvreCutoutsEditorToolbar
                panelTypeId={panel.panelTypeId}
                louvreIndex={selection.index}
                cutouts={cutouts}
                isSaveAvailable={isUnsaved && !isWithIntersections}
            />
            {isSaveInProgress &&
                ReactDOM.createPortal(
                    <ProcessingPane
                        isVisible={true}
                        message="Saving louvre curtain cutouts..."
                        title="Louvre curtain cutouts"
                    />,
                    rootElement,
                )}
        </>
    );
};
